import React from 'react';

const SkeletonLoader = ({ type = 'card', count = 1, className = '' }) => {
  const renderCardSkeleton = () => (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse ${className}`}>
      <div className="w-full h-48 bg-gray-200"></div>
      <div className="p-6">
        <div className="flex flex-wrap gap-2 mb-3">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
          <div className="h-6 bg-gray-200 rounded-full w-14"></div>
        </div>
        <div className="h-6 bg-gray-200 rounded-lg mb-3 w-3/4"></div>
        <div className="space-y-2">
          <div className="h-4 bg-gray-200 rounded w-full"></div>
          <div className="h-4 bg-gray-200 rounded w-5/6"></div>
          <div className="h-4 bg-gray-200 rounded w-4/6"></div>
        </div>
      </div>
    </div>
  );

  const renderCompactCardSkeleton = () => (
    <div className={`bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 animate-pulse relative ${className}`}>
      <div className="w-full h-48 bg-gray-200"></div>
      <div className="p-4 pb-16">
        <div className="h-6 bg-gray-200 rounded mb-2"></div>
        <div className="h-4 bg-gray-200 rounded mb-3 w-3/4"></div>
        <div className="flex gap-2 mb-3">
          <div className="h-6 bg-gray-200 rounded-full w-16"></div>
          <div className="h-6 bg-gray-200 rounded-full w-20"></div>
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="h-8 bg-gray-200 rounded w-full"></div>
      </div>
    </div>
  );

  const renderProfessionalSkeleton = () => (
    <div className={`group text-center relative p-6 pb-20 ${className}`}>
      <div className="relative mb-6">
        <div className="w-32 h-32 mx-auto rounded-full bg-gray-200 animate-pulse"></div>
      </div>
      <div className="h-6 bg-gray-200 rounded mb-2 w-3/4 mx-auto animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded mb-4 w-1/2 mx-auto animate-pulse"></div>
      <div className="absolute bottom-0 left-0 right-0 p-4">
        <div className="h-8 bg-gray-200 rounded w-full animate-pulse"></div>
      </div>
    </div>
  );

  const renderHeaderSkeleton = () => (
    <div className={`text-center mb-16 ${className}`}>
      <div className="inline-flex items-center justify-center p-2 mb-6 rounded-full bg-gray-200 animate-pulse">
        <div className="w-6 h-6 rounded bg-gray-300"></div>
      </div>
      <div className="h-12 bg-gray-200 rounded-lg mx-auto mb-6 w-96 animate-pulse"></div>
      <div className="h-6 bg-gray-200 rounded-lg mx-auto w-3/4 animate-pulse"></div>
    </div>
  );

  const renderProviderSkeleton = () => (
    <section className="py-16 pt-40 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 mb-10 animate-pulse">
          <div className="w-full h-96 bg-gray-200"></div>
        </div>

        {/* Provider Information Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 mb-10 animate-pulse">
          <div className="mb-6">
            {/* Title */}
            <div className="h-12 bg-gray-200 rounded-lg mb-4 w-3/4"></div>

            {/* Categories */}
            <div className="flex flex-wrap gap-2 mb-6">
              <div className="h-8 bg-gray-200 rounded-full w-20"></div>
              <div className="h-8 bg-gray-200 rounded-full w-24"></div>
              <div className="h-8 bg-gray-200 rounded-full w-16"></div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2 w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2 w-12"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <div className="w-6 h-6 bg-gray-200 rounded"></div>
              <div className="flex-1">
                <div className="h-4 bg-gray-200 rounded mb-2 w-14"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="border-t border-gray-200 pt-6">
            <div className="h-6 bg-gray-200 rounded mb-3 w-32"></div>
            <div className="space-y-2">
              <div className="h-4 bg-gray-200 rounded w-full"></div>
              <div className="h-4 bg-gray-200 rounded w-5/6"></div>
              <div className="h-4 bg-gray-200 rounded w-4/6"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>

        {/* Widget Container Skeleton */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4 w-48"></div>
          <div className="space-y-4">
            <div className="h-4 bg-gray-200 rounded w-full"></div>
            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
            <div className="h-4 bg-gray-200 rounded w-4/6"></div>
          </div>
        </div>
      </div>
    </section>
  );

  const renderSkeleton = () => {
    switch (type) {
      case 'card':
        return renderCardSkeleton();
      case 'compactCard':
        return renderCompactCardSkeleton();
      case 'professional':
        return renderProfessionalSkeleton();
      case 'header':
        return renderHeaderSkeleton();
      case 'provider':
        return renderProviderSkeleton();
      default:
        return renderCardSkeleton();
    }
  };

  if (count === 1) {
    return renderSkeleton();
  }

  return (
    <>
      {Array.from({ length: count }, (_, index) => (
        <div key={index}>{renderSkeleton()}</div>
      ))}
    </>
  );
};

export default SkeletonLoader;
